import { useState, useRef, useEffect, useCallback } from 'react';
import { useAnimatedNavigate } from '../hooks/useAnimatedNavigate';
import { useAuth } from '../hooks/useAuth';

function getInitials(name: string | null, email: string | null): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
    }
    return name[0]!.toUpperCase();
  }
  if (email) return email[0]!.toUpperCase();
  return '?';
}

export function UserAvatarDropdown() {
  const { name, email, isAdmin, logout } = useAuth();
  const accessTierLabel = isAdmin ? 'admin' : 'analyst';
  const navigate = useAnimatedNavigate();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const initials = getInitials(name, email);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        close();
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, close]);

  return (
    <div className="avatar-dropdown" ref={wrapperRef}>
      <button
        className="topbar-avatar"
        title="Account"
        aria-label="Account menu"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="topbar-avatar-initial">{initials}</span>
      </button>

      {open && (
        <div className="avatar-dropdown-menu">
          <div className="avatar-dropdown-user">
            <span className="avatar-dropdown-name">{name ?? 'User'}</span>
            {email && <span className="avatar-dropdown-email">{email}</span>}
            {email && <span className="avatar-dropdown-role">{accessTierLabel}</span>}
          </div>

          <div className="avatar-dropdown-session">
            <span className="avatar-dropdown-session-dot" />
            <span>Active session</span>
          </div>

          <div className="avatar-dropdown-divider" />

          <button
            className="avatar-dropdown-item"
            onClick={() => { close(); navigate('/settings'); }}
          >
            Settings
          </button>
          <button
            className="avatar-dropdown-item avatar-dropdown-logout"
            onClick={() => { close(); void logout(); }}
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
